import { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import L from "leaflet";
import type { MarkerClusterGroup } from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import api from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import LoadingSplash from "@/components/LoadingSplash";
import "./ReportsCoverage.css";

type MapExtent = {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
};

type MapPoint = {
  address_id: number;
  latitude: number;
  longitude: number;
  status: string;
  user_id: number;
  timestamp: string;
};

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

async function fetchMapExtent(): Promise<MapExtent> {
  const res = await api.get("/map/extent/");
  return res.data;
}

async function fetchMapPoints(bbox: string): Promise<MapPoint[]> {
  const res = await api.get("/map/points/", { params: { bbox } });
  return res.data;
}

const isGreenStatus = (status: string) => status === "REGULAR" || status === "CUSTOMER";

const createMarkerIcon = (status: string) =>
  L.divIcon({
    className: `coverage-marker leaflet-div-icon ${
      isGreenStatus(status) ? "coverage-marker--green" : "coverage-marker--red"
    }`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -8],
  });

export default function ReportsCoverageClusters() {
  const { user, loading: authLoading } = useAuth();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const clusterRef = useRef<MarkerClusterGroup | null>(null);
  const [bbox, setBbox] = useState<string>("");

  const extentQuery = useQuery({
    queryKey: ["coverage-extent"],
    queryFn: fetchMapExtent,
    staleTime: 5 * 60_000,
  });

  const debouncedUpdateBounds = useMemo(
    () =>
      debounce(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = map.getBounds();
        const west = bounds.getWest();
        const south = bounds.getSouth();
        const east = bounds.getEast();
        const north = bounds.getNorth();
        setBbox(`${west},${south},${east},${north}`);
      }, 300),
    []
  );

  useEffect(() => {
    return () => debouncedUpdateBounds.cancel();
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, { preferCanvas: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);

    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: false,
      iconCreateFunction: (clusterGroup) => {
        const bounds = clusterGroup.getBounds();
        const map = mapRef.current;
        let spreadPx = 30;
        if (map) {
          const sw = map.latLngToLayerPoint(bounds.getSouthWest());
          const ne = map.latLngToLayerPoint(bounds.getNorthEast());
          spreadPx = Math.max(Math.abs(ne.x - sw.x), Math.abs(ne.y - sw.y));
        }
        const size = Math.max(32, Math.min(80, 32 + spreadPx * 0.12));
        const count = clusterGroup.getChildCount();
        const intensity = Math.min(1, count / 150);
        const color = `rgba(67, 160, 71, ${0.35 + intensity * 0.5})`;

        return L.divIcon({
          html: `<div class="coverage-cluster" style="width:${size}px;height:${size}px;background:${color}"><span>${count}</span></div>`,
          className: "coverage-cluster-wrapper",
          iconSize: [size, size],
        });
      },
    });

    mapRef.current = map;
    clusterRef.current = cluster;

    map.addLayer(cluster);
    map.on("moveend", debouncedUpdateBounds);
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    const map = mapRef.current;
    const bounds = extentQuery.data;
    if (!map || !bounds) return;

    const fitBounds = L.latLngBounds(
      [bounds.min_lat, bounds.min_lng],
      [bounds.max_lat, bounds.max_lng]
    );
    map.fitBounds(fitBounds, { padding: [24, 24] });
    debouncedUpdateBounds();
  }, [extentQuery.data, debouncedUpdateBounds]);

  const pointsQuery = useQuery<MapPoint[]>({
    queryKey: ["coverage-points", bbox],
    queryFn: () => fetchMapPoints(bbox),
    enabled: !!bbox,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;

    cluster.clearLayers();
    const points: MapPoint[] = pointsQuery.data ?? [];
    points.forEach((point) => {
      const marker = L.marker([point.latitude, point.longitude], {
        icon: createMarkerIcon(point.status),
      });
      marker.bindPopup(
        `<div class="coverage-popup">
          <div><strong>Status</strong> ${point.status}</div>
          <div><strong>Timestamp</strong> ${new Date(point.timestamp).toLocaleString()}</div>
          <div><strong>User ID</strong> ${point.user_id}</div>
        </div>`
      );
      cluster.addLayer(marker);
    });
  }, [pointsQuery.data]);

  useEffect(() => {
    return () => {
      mapRef.current?.off();
      mapRef.current?.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, []);

  if (authLoading) return <LoadingSplash />;
  if (!user?.is_superuser) return <Navigate to="/search" replace />;

  const showMapSpinner = extentQuery.isPending || (pointsQuery.isPending && !pointsQuery.data);

  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Dynamic clusters resize based on how spread out their child markers are.
          </Typography>
          <Box className="coverage-map-container">
            <div ref={mapContainerRef} className="coverage-map" />
            {showMapSpinner && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.65)",
                  zIndex: 500,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
